import { differenceInMilliseconds } from "date-fns";
import { generateReminderString, getTaskContentName } from "../../model";
import { useNavigate } from "react-router-dom";
import {
  BetaType,
  IBatch,
  IParticipant,
  IStopwatchGarminStream,
  IStopwatchMovesenseStream,
  ITask,
  ReminderItem,
  TaskType,
} from "../../shared/db";
import CronParser from "cron-parser";
import {
  useSelectProjectAdherenceData,
  useSelectBatchData,
  useSelectMethod,
  useSelectTheme,
  useSelectGarminDirectParticipantDataDigestMap,
  useSelectGarminConnectParticipantDataDigestMap,
  useSelectParticipantIssue,
  useSelectSettings,
  ThemeState,
} from "../../store";
import {
  BellIcon,
  DeviceIcon,
  GarminConnectIcon,
  InviteParticipantBackground,
  LabfrontIcon,
  HeatmapLowWearTimeBG,
  MovesenseImg,
  ParticipantTaskIcon,
  RepeatIcon,
  SurveyBlackIcon,
  TimerBlackIcon,
  NotJoinedBG,
  NotificationBlackIcon,
  BlackTriangleIcon,
  NotSyncedIcon,
  CheckBlackIcon,
} from "../../asset/image";

import {
  useCreateTaskColorTranslator,
  isInBetaType,
  RIF,
  setAlpha,
  sortBy,
  splitTasks,
  useCurrentProjectState,
} from "../../engine";
import {
  Button,
  ButtonReverse,
  ButtonText,
  Checkbox,
  convertGarminStreamDescription,
  convertMovesenseDescription,
  OverviewSidebar,
  OverviewSidebarDataType,
  OverviewSidebarInCellTabTitle,
  OverviewSidebarTab,
  ParticipantNotificationDiscardPop,
  ParticipantNotificationInfoPop,
  PopupSendNotification,
  PopupSize,
  PopupTemplate,
  ProjectSummaryPage,
  ProjectSwitchBar,
  TaskCard,
  TaskScheduleCard,
  Tooltip,
} from "..";
import { CSSProperties, RefObject, useEffect, useMemo, useState } from "react";
import _, { omit } from "lodash";
import {
  AttemptRequestParticipantStatus,
  RequestGarminDataDigestPayload,
} from "./request_project_data_hook";
import { EventIcon } from "../../lib/svg/icon/event";
import { CalendarIcon } from "../../lib/svg/icon/calendar";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";

const MILLISECONDS_IN_ONE_HOUR = 3600000;
const MINUTES_IN_ONE_HOUR = 60;
type HeatmapData = Record<string, Record<number, number | undefined>>;
interface DailyStatusItem {
  gt4: string[];
  lt4: string[];
  noData: string[];
  notSynced: string[];
}
interface DailyStatus {
  [yymmdd: number]: DailyStatusItem;
}

interface ParticipantActivated extends IParticipant {
  activatedYymmdd: number;
}

export const CopyParticipantListButton = (props: {
  filteredParticipantList: IParticipant[];
  color: ThemeState["color"];
}) => {
  const { filteredParticipantList, color } = props;
  const [showPopup, setShowPopup] = useState(false);

  if (filteredParticipantList.length === 0) return null;

  const copyParticipantList = () => {
    const copyContent = filteredParticipantList.map((p) => p.insignia).join(", ");
    navigator.clipboard.writeText(copyContent);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 1500);
  };

  return (
    <div
      css={{
        alignItems: "center",
      }}
    >
      <AnimatePresence>
        {showPopup && (
          <motion.div
            transition={{ duration: 0.2 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            css={{
              position: "absolute",
              zIndex: "999",
              top: -30,
              left: 90,
              width: "75px",
              background: color.surface.dark,
              borderRadius: "5px",
              color: color.white,
              padding: "8px 12px",
              textAlign: "center",
              mixBlendMode: "normal",
            }}
          >
            Copied!
          </motion.div>
        )}
      </AnimatePresence>

      <ButtonText
        {...{
          onClick: copyParticipantList,
          textColor: color.textIcon.link,
          textColorWhileHovered: color.surface.hover.blue,
          backgroundColorWhileHovered: color.surface.hover.hoverWhiteBg,
        }}
      >
        Copy The Participant List
      </ButtonText>
    </div>
  );
};

export const OverviewPageV2 = () => {
  const { color, fontSize, fontWeight } = useSelectTheme();
  const colorTranslator = useCreateTaskColorTranslator();
  const { projectId, project } = useCurrentProjectState();
  const navigate = useNavigate();
  const batchId = _.head(project?.batchList)?.id ?? "";
  const batchData = useSelectBatchData();
  const batch = batchData[batchId] ?? ({} as IBatch);
  const participantList = batch.participantList ?? [];
  const method = useSelectMethod() ?? {};
  const projectAdherence = useSelectProjectAdherenceData(projectId);
  const adherenceMap = projectAdherence?.participantAdherence ?? {};
  const taskList: ITask[] =
    method.taskList?.filter((item: ITask) => item.enabled).sort(sortBy("index")) ?? [];
  const settings = useSelectSettings();
  const [displaySummaryPage, setDisplaySummaryPage] = useState(false);
  const [displayPopupSendNotification, setDisplayPopupSendNotification] = useState<boolean>(false);
  const [sendNotificationParticipantIdList, setSendNotificationParticipantIdList] = useState<
    string[]
  >([]);
  const [renderFullList, setRenderFullList] = useState(false);

  const [expandSidebar, setExpandSidebar] = useState(false);
  const [sidebarDatatype, setSidebarDatatype] = useState<OverviewSidebarDataType>("heatmap");
  const [sidebarDefaultTab, setSidebarDefaultTab] = useState<OverviewSidebarTab>(
    OverviewSidebarTab.InCell,
  );
  const [sidebarDeviceType, setSidebarDeviceType] = useState<
    "garminConnect" | "garminDirect" | null
  >(null);
  const [sidebarDate, setSidebarDate] = useState<number>(0);
  const [sidebarInCellTabTitle, setSidebarInCellTabTitle] = useState<OverviewSidebarInCellTabTitle>(
    OverviewSidebarInCellTabTitle.Gt4,
  );
  const [participantInCell, setParticipantInCell] = useState<string[]>([]);
  const [selectedParticipantList, setSelectedParticipantList] = useState<string[]>([]);
  const [selectedCell, setSelectedCell] = useState<string | null>(null);
  const [filterByIssue, setFilterByIssue] = useState<number | null>(null);
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);

  // handle click outside of sidebar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const sidebarElement = document.getElementById("overview-sidebar");
      const notJoinedYetButton = document.getElementById("not-joined-yet-button");
      const notSetupGarminConnectButton = document.getElementById(
        "not-setup-garmin-connect-button",
      );
      const notSetupLabfrontDirectButton = document.getElementById(
        "not-setup-labfront-direct-button",
      );
      if (sidebarElement && sidebarElement.contains(target)) return;
      if (target.id.startsWith("daily-quality-cell-")) return;
      if (notJoinedYetButton && notJoinedYetButton.contains(target)) return;
      if (notSetupGarminConnectButton && notSetupGarminConnectButton.contains(target)) return;
      if (notSetupLabfrontDirectButton && notSetupLabfrontDirectButton.contains(target)) return;
      setExpandSidebar(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const dateToYymmdd = (date?: Date | string) => {
    const d = new Date(date ?? Date.now());
    const yy = String(d.getFullYear()).slice(-2);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return Number(`${yy}${mm}${dd}`);
  };

  const yymmddIndexList = Array(7)
    .fill(0)
    .map((_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - 6 + index);
      return dateToYymmdd(date);
    });

  const garminConnectEnabled = method?.garminConnectEnable ?? false;
  const garminDeviceEnabled = useMemo(() => {
    return taskList.some((item) => item.type === TaskType.GarminDevice && item.enabled);
  }, [taskList]);

  const garminDeviceBbiEnabled = useMemo(() => {
    return taskList.some(
      (item) =>
        item.type === TaskType.GarminDevice &&
        item.enabled &&
        (item.garminDevice?.bbiEnable || item.garminDevice?.enhancedBbiEnable),
    );
  }, [taskList]);

  const participantIssue = useSelectParticipantIssue();
  const participantIssueList = useMemo(() => {
    if (participantIssue?.issuesIndexMap) {
      return Object.values(participantIssue.issuesIndexMap).filter((v) => {
        if (v === "notActivated") return false;
        if (!garminConnectEnabled) {
          if (v.includes("garminConnect") || v.includes("GarminConnect")) return false;
        }
        if (!garminDeviceEnabled) {
          if (v.includes("garminDirect") || v.includes("GarminDevice")) return false;
        }
        return true;
      });
    }
    return [];
  }, [participantIssue, garminConnectEnabled, garminDeviceEnabled]);

  const renderAttentionOverview = useMemo(() => {
    if (!project?.createdAt) return false;
    if (!participantList.length) return false;

    // particiipant may not activated yest but already connect garmin connect
    if (participantIssue?.garminConnectLinkDuplicatedParticiipantSetList) return true;

    const activatedParticipantIds = participantList.filter((p) => p.activated).map((p) => p.id);
    const activatedParticipantsWithIssues = Object.keys(
      participantIssue.participantIssuesMap,
    ).filter((id) => activatedParticipantIds.includes(id));
    if (!activatedParticipantsWithIssues.length) return false;

    return true;
  }, [participantList, project, participantIssue]);

  const participantNotJoinedTheProject = useMemo(() => {
    return participantList.filter((p) => !p.activated).map((p) => p.id);
  }, [participantList]);

  const participantNotSetupGarminConnect = useMemo(() => {
    return participantList.filter((p) => p.activated && !p.garminConnectAccountId).map((p) => p.id);
  }, [participantList]);

  const participantNotSetupLabfrontDirect = useMemo(() => {
    const participantAdherenceList = projectAdherence?.participantAdherence
      ? Object.values(projectAdherence.participantAdherence)
      : [];
    return participantAdherenceList
      .filter((p) => !p.status.deviceType)
      .map((p) => p.participantId)
      .filter((pid) => {
        if (garminConnectEnabled) {
          return (
            !participantNotJoinedTheProject.includes(pid) &&
            !participantNotSetupGarminConnect.includes(pid)
          );
        }
        return !participantNotJoinedTheProject.includes(pid);
      });
  }, [
    projectAdherence?.participantAdherence,
    garminConnectEnabled,
    participantNotJoinedTheProject,
    participantNotSetupGarminConnect,
  ]);

  // const participantActivated = []
  const participantActivated = useMemo<ParticipantActivated[]>(() => {
    return participantList
      .filter((p) => p.activated)
      .map((p) => {
        const participantAdherence = adherenceMap[p.id];
        const activatedYymmdd = +moment
          .tz(new Date(p.activatedDate), participantAdherence?.status.timezone)
          .format("yMMDD")
          .slice(2);
        return { ...p, activatedYymmdd };
      });
  }, [participantList, adherenceMap]);
  // const participantFullySetup = useMemo(() => {
  //   return participantList
  //     .filter((p) => {
  //       if (garminConnectEnabled) {
  //         return p.activated && p.garminConnectAccountId
  //       }
  //       return p.activated
  //     })
  //     .filter((p) => {
  //       if (garminDeviceEnabled) {
  //         return projectAdherence.adherenceList?.some((a) => a.participantId === p.id && a.status.deviceType)
  //       }
  //       return true
  //     })
  // }, [participantList, garminConnectEnabled, garminDeviceEnabled, projectAdherence.adherenceList])

  const requestGarminDataDigestPayload = useMemo<RequestGarminDataDigestPayload>(() => {
    return {
      yymmddIndexStart: yymmddIndexList[0],
      yymmddIndexEnd: yymmddIndexList[yymmddIndexList.length - 1],
    };
  }, []);

  AttemptRequestParticipantStatus({
    // requestAdherence: requestAdherencePayload,
    requestGarminDirectBbiDigest: garminDeviceEnabled ? requestGarminDataDigestPayload : undefined,
    requestGarminConnectWearTime: garminConnectEnabled ? requestGarminDataDigestPayload : undefined,
    requestParticipantIssues: true,
  });

  const formatYymmddToLabel = (yymmdd: number) => {
    const str = yymmdd.toString();
    const year = 2000 + parseInt(str.slice(0, 2), 10);
    const month = parseInt(str.slice(2, 4), 10) - 1;
    const day = parseInt(str.slice(4, 6), 10);

    const date = new Date(year, month, day);
    const monthStr = date.toLocaleString("en-US", { month: "short" });
    const dayStr = date.getDate().toString();

    return [monthStr, dayStr];
  };

  // const generateReminderString = (reminder: ReminderItem) => {
  //   if (reminder.type === 'cron') {
  //     const cronInterval = CronParser.parseExpression(reminder.value)
  //     return formatCronString(cronInterval)
  //   } else if (reminder.type === 'date') {
  //     return formatISOString(reminder.value)
  //   }
  // }

  // const formatCronString = (cronParseObj: CronParser.CronExpression<false>) => {
  //   const {dayOfWeek, hour, minute} = cronParseObj.fields

  //   const timeString = `${hour[0].toString().padStart(2, '0')}:${minute[0].toString().padStart(2, '0')}`
  //   const AMPM = hour[0] < 12 ? 'AM' : 'PM'

  //   // (0 or 7 is Sun) from 'cron-parser'
  //   const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  //   const formattedDaysOfWeek = dayOfWeek.map((index: number) => daysOfWeek[index])

  //   let formattedString = 'Repeats'

  //   if (dayOfWeek.length >= 7) {
  //     formattedString += ` daily at ${timeString} ${AMPM}`
  //   } else if (dayOfWeek.length === 1) {
  //     formattedString += ` every ${formattedDaysOfWeek[0]} at ${timeString} ${AMPM}`
  //   } else {
  //     const lastDayOfWeek = formattedDaysOfWeek.pop()
  //     formattedString += ` every ${formattedDaysOfWeek.join(', ')} and ${lastDayOfWeek} at ${timeString} ${AMPM}`
  //   }
  //   return formattedString
  // }

  // const formatISOString = (dateString: string) => {
  //   const utcDate = new Date(dateString)
  //   const options: Intl.DateTimeFormatOptions = {
  //     month: 'long',
  //     day: 'numeric',
  //     year: 'numeric',
  //     hour: 'numeric',
  //     minute: 'numeric',
  //     hour12: true,
  //   }
  //   const timezoneOffset = new Date().getTimezoneOffset() * 60 * 1000
  //   return new Date(utcDate.getTime() + timezoneOffset).toLocaleString(undefined, options)
  // }

  // calculating garmin connect data
  const garminConnectParticipantDataDigestMap =
    useSelectGarminConnectParticipantDataDigestMap(projectId);
  const [garminConnectDailyStatus, setGarminConnectDailyStatus] = useState<DailyStatus>({});
  const [garminConnectHeatmapData, setGarminConnectHeatmapData] = useState<HeatmapData>({});
  useEffect(() => {
    if (participantActivated.length === 0) return;
    if (Object.keys(adherenceMap).length === 0) return;
    if (!garminConnectParticipantDataDigestMap) {
      return setGarminConnectDailyStatus(
        Object.fromEntries(
          yymmddIndexList.map((y) => [
            y,
            { gt4: [], lt4: [], noData: [], notSynced: participantActivated.map((p) => p.id) },
          ]),
        ),
      );
    }
    if (Object.keys(garminConnectParticipantDataDigestMap).length === 0) return;
    const tempGarminConnectDailyStatus: DailyStatus = Object.fromEntries(
      yymmddIndexList.map((y) => [y, { gt4: [], lt4: [], noData: [], notSynced: [] }]),
    );
    const tempGarminConnectHeatmapData: HeatmapData = {};
    participantActivated.forEach((p) => {
      const garminConnectDigestData = garminConnectParticipantDataDigestMap[p.id];
      const participantAdherence = adherenceMap[p.id];
      const garminConnectLastSyncYymmdd = participantAdherence?.status
        .unixTimestampLastGarminConnectSync
        ? +moment
            .tz(
              new Date(participantAdherence?.status.unixTimestampLastGarminConnectSync),
              participantAdherence?.status.timezone,
            )
            .format("yMMDD")
            .slice(2)
        : undefined;

      tempGarminConnectHeatmapData[p.id] = Object.fromEntries(
        yymmddIndexList.map((y) => [y, undefined]),
      );
      yymmddIndexList.forEach((yymmdd) => {
        if (yymmdd >= p.activatedYymmdd) {
          const yymmddBeforeLastSync = garminConnectLastSyncYymmdd
            ? yymmdd <= garminConnectLastSyncYymmdd
            : false;

          if (yymmddBeforeLastSync) {
            const wearTime = garminConnectDigestData?.[yymmdd]?.wearTime ?? 0;
            tempGarminConnectHeatmapData[p.id][yymmdd] = wearTime;
            if (wearTime === 0) return tempGarminConnectDailyStatus[yymmdd].noData.push(p.id);
            const wearTimeHours = wearTime / MINUTES_IN_ONE_HOUR;
            if (wearTimeHours <= 4) {
              tempGarminConnectDailyStatus[yymmdd].lt4.push(p.id);
            } else {
              tempGarminConnectDailyStatus[yymmdd].gt4.push(p.id);
            }
          } else {
            tempGarminConnectDailyStatus[yymmdd].notSynced.push(p.id);
          }
        }
      });
    });
    setGarminConnectDailyStatus(tempGarminConnectDailyStatus);
    setGarminConnectHeatmapData(tempGarminConnectHeatmapData);
  }, [garminConnectParticipantDataDigestMap, participantActivated, adherenceMap]);

  // calculating garmin direct data
  const garminDirecParticipantDatatDigestMap =
    useSelectGarminDirectParticipantDataDigestMap(projectId);
  const [garminDirectDailyStatus, setGarminDirectDailyStatus] = useState<DailyStatus>({});
  const [garminDirectHeatmapData, setGarminDirectHeatmapData] = useState<HeatmapData>({});
  useEffect(() => {
    if (participantActivated.length === 0) return;
    if (Object.keys(adherenceMap).length === 0) return;
    if (!garminDirecParticipantDatatDigestMap) {
      return setGarminDirectDailyStatus(
        Object.fromEntries(
          yymmddIndexList.map((y) => [
            y,
            { gt4: [], lt4: [], noData: [], notSynced: participantActivated.map((p) => p.id) },
          ]),
        ),
      );
    }
    if (Object.keys(garminDirecParticipantDatatDigestMap).length === 0) return;
    if (!garminDirecParticipantDatatDigestMap) return;
    const tempGarminDirectDailyStatus: DailyStatus = Object.fromEntries(
      yymmddIndexList.map((y) => [y, { gt4: [], lt4: [], noData: [], notSynced: [] }]),
    );
    const tempGarminDirectHeatmapData: HeatmapData = {};
    participantActivated.forEach((p) => {
      const garminDirectDigestData = garminDirecParticipantDatatDigestMap[p.id];
      const participantAdherence = adherenceMap[p.id];
      tempGarminDirectHeatmapData[p.id] = Object.fromEntries(
        yymmddIndexList.map((y) => [y, undefined]),
      );
      const garminDirectLastSyncYymmdd = participantAdherence?.status
        .unixTimestampLastGarminDeviceSync
        ? +moment
            .tz(
              new Date(participantAdherence?.status.unixTimestampLastGarminDeviceSync),
              participantAdherence?.status.timezone,
            )
            .format("yMMDD")
            .slice(2)
        : undefined;

      yymmddIndexList.forEach((yymmdd) => {
        if (yymmdd >= p.activatedYymmdd) {
          const yymmddBeforeLastSync = garminDirectLastSyncYymmdd
            ? yymmdd <= garminDirectLastSyncYymmdd
            : false;
          if (yymmddBeforeLastSync) {
            const wearTime = garminDirectDigestData?.[yymmdd]?.wearTime ?? 0;
            tempGarminDirectHeatmapData[p.id][yymmdd] = wearTime;
            if (wearTime === 0) return tempGarminDirectDailyStatus[yymmdd].noData.push(p.id);
            const wearTimeHours = wearTime / MILLISECONDS_IN_ONE_HOUR;
            if (wearTimeHours <= 4) {
              tempGarminDirectDailyStatus[yymmdd].lt4.push(p.id);
            } else {
              tempGarminDirectDailyStatus[yymmdd].gt4.push(p.id);
            }
          } else {
            tempGarminDirectDailyStatus[yymmdd].notSynced.push(p.id);
          }
        }
      });
    });
    setGarminDirectDailyStatus(tempGarminDirectDailyStatus);
    setGarminDirectHeatmapData(tempGarminDirectHeatmapData);
  }, [garminDirecParticipantDatatDigestMap, participantActivated, adherenceMap]);

  const OverviewBlockTemplate = (props: {
    style?: CSSProperties;
    children?: React.ReactNode;
    ref?: RefObject<HTMLDivElement>;
  }) => {
    const { children, style, ref } = props;
    return (
      <div
        ref={ref}
        css={{
          ...style,
          width: "100%",
          padding: 16,
          backgroundColor: color.surface.white,
          borderRadius: "5px",
          boxShadow: `0px 4px 12px 0px rgba(212, 212, 212, 0.25)`,
          display: "inline-flex",
          alignSelf: "start",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {children}
      </div>
    );
  };

  const ParticipantSetupStatus = () => {
    const participantsGarminConnected = participantList.filter(
      (p) => p.activated && p.garminConnectAccountId,
    ).length;
    const participantAdherenceList = projectAdherence?.participantAdherence
      ? Object.values(projectAdherence.participantAdherence)
      : [];
    const participantsLabfrontDirectConnected = participantAdherenceList.filter(
      (a) => a.status.deviceType,
    ).length;
    const INITIAL_BAR_HEIGHT = 48;
    const calculateHeightOfBar = (count: number) => {
      return (INITIAL_BAR_HEIGHT * count) / (participantList.length || 1);
    };

    const handleClick = (defaultTab: OverviewSidebarTab) => {
      setSelectedCell(null);
      setSidebarDatatype("participantSetupStatus");
      setSidebarDate(dateToYymmdd());
      setExpandSidebar(true);
      setSidebarDefaultTab(defaultTab);
    };

    return (
      <OverviewBlockTemplate {...{ style: { minHeight: 240 } }}>
        <>
          {/* title */}
          <div css={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span css={{ fontWeight: fontWeight.thick }}>Participant device setup status</span>
            <Tooltip content="Keep track of participant setup status here." />
          </div>
          {/* content */}
          <div css={{ display: "flex", width: "100%" }}>
            {/* total participants */}
            <div
              css={{
                flex: 1,
              }}
            >
              <div
                css={{
                  height: 123,
                  minHeight: 123,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderRight: `1px solid ${color.border.default}`,
                }}
              >
                {/* text */}
                <div
                  css={{
                    width: "calc(100% - 8px)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    padding: 4,
                    borderRadius: 4,
                    cursor: "pointer",
                    ":hover": {
                      backgroundColor: color.surface.hover.hoverWhiteBg,
                    },
                  }}
                >
                  <span css={{ fontSize: fontSize.h5, fontWeight: fontWeight.thick }}>
                    {participantList.length}
                  </span>
                  <p
                    css={{
                      fontSize: fontSize.h8,
                      color: color.textIcon.secondary,
                    }}
                  >
                    Participants
                  </p>
                </div>
                {/* bar */}
                <div
                  css={{
                    width: "100%",
                    height: 48,
                    backgroundColor: color.surface.blue.brand,
                    opacity: 0.45,
                  }}
                />
              </div>
            </div>
            {/* count of participants joined the project */}
            <div
              css={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div
                css={{
                  height: 123,
                  minHeight: 123,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderRight: `1px solid ${color.border.default}`,
                }}
              >
                {/* text */}
                <div
                  css={{
                    width: "calc(100% - 8px)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    padding: 4,
                    borderRadius: 4,
                    cursor: "pointer",
                    ":hover": {
                      backgroundColor: color.surface.hover.hoverWhiteBg,
                    },
                  }}
                >
                  <span css={{ fontSize: fontSize.h5, fontWeight: fontWeight.thick }}>
                    {project?.activatedParticipantCount ?? 0}
                  </span>
                  <p
                    css={{
                      fontSize: fontSize.h8,
                      color: color.textIcon.secondary,
                    }}
                  >
                    Joined this project
                  </p>
                </div>
                {/* bar */}
                <div
                  css={{
                    width: "100%",
                    height: calculateHeightOfBar(project?.activatedParticipantCount ?? 0),
                    backgroundColor: color.surface.blue.brand,
                    opacity: 0.6,
                  }}
                />
              </div>
              {participantNotJoinedTheProject.length > 0 && (
                <button
                  type="button"
                  id="not-joined-yet-button"
                  onClick={() => handleClick(OverviewSidebarTab.NotJoinedYet)}
                  css={{
                    width: "calc(100% - 8px)",
                    border: "none",
                    backgroundColor: "transparent",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    padding: 4,
                    borderRadius: 4,
                    cursor: "pointer",
                    ":hover": {
                      backgroundColor: color.surface.hover.hoverWhiteBg,
                    },
                  }}
                >
                  <span css={{ fontWeight: fontWeight.thick, color: color.textIcon.link }}>
                    {participantNotJoinedTheProject.length}
                  </span>
                  <span
                    css={{
                      fontSize: fontSize.h8,
                      color: color.textIcon.secondary,
                    }}
                  >
                    Not joined yet
                  </span>
                </button>
              )}
            </div>
            {/* count of participants finished setting up garmin connect */}
            {RIF(
              garminConnectEnabled,
              <div
                css={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div
                  css={{
                    height: 123,
                    minHeight: 123,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderRight: `1px solid ${color.border.default}`,
                  }}
                >
                  {/* text */}
                  <div
                    css={{
                      width: "calc(100% - 8px)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      padding: 4,
                      borderRadius: 4,
                      cursor: "pointer",
                      ":hover": {
                        backgroundColor: color.surface.hover.hoverWhiteBg,
                      },
                    }}
                  >
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <img src={GarminConnectIcon} width="16" alt="Garmin Connect icon" />
                      <span css={{ fontSize: fontSize.h5, fontWeight: fontWeight.thick }}>
                        {participantsGarminConnected}
                      </span>
                    </div>
                    <p
                      css={{
                        fontSize: fontSize.h8,
                        color: color.textIcon.secondary,
                      }}
                    >
                      Setup Garmin Connect
                    </p>
                  </div>
                  {/* bar */}
                  <div
                    css={{
                      width: "100%",
                      height: calculateHeightOfBar(participantsGarminConnected),
                      backgroundColor: color.surface.blue.brand,
                      opacity: 0.75,
                    }}
                  />
                </div>
                {participantNotSetupGarminConnect.length > 0 && (
                  <button
                    type="button"
                    id="not-setup-garmin-connect-button"
                    onClick={() => handleClick(OverviewSidebarTab.NotStartedYet)}
                    css={{
                      width: "calc(100% - 8px)",
                      border: "none",
                      backgroundColor: "transparent",
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      padding: 4,
                      borderRadius: 4,
                      cursor: "pointer",
                      ":hover": {
                        backgroundColor: color.surface.hover.hoverWhiteBg,
                      },
                    }}
                  >
                    <span css={{ fontWeight: fontWeight.thick, color: color.textIcon.link }}>
                      {participantNotSetupGarminConnect.length}
                    </span>
                    <span
                      css={{
                        fontSize: fontSize.h8,
                        color: color.textIcon.secondary,
                      }}
                    >
                      Device setup not started
                    </span>
                  </button>
                )}
              </div>,
            )}
            {/* count of participants finished setting up labfront direct */}
            {RIF(
              garminDeviceEnabled,
              <div
                css={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div
                  css={{
                    height: 123,
                    minHeight: 123,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {/* text */}
                  <div
                    css={{
                      width: "calc(100% - 8px)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      padding: 4,
                      borderRadius: 4,
                      cursor: "pointer",
                      ":hover": {
                        backgroundColor: color.surface.hover.hoverWhiteBg,
                      },
                    }}
                  >
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <img src={LabfrontIcon} width="16" alt="Labfront Direct icon" />
                      <span css={{ fontSize: fontSize.h5, fontWeight: fontWeight.thick }}>
                        {participantsLabfrontDirectConnected}
                      </span>
                    </div>
                    <span css={{ fontSize: fontSize.h5, fontWeight: fontWeight.thick }}>{}</span>
                    <p
                      css={{
                        fontSize: fontSize.h8,
                        color: color.textIcon.secondary,
                      }}
                    >
                      Setup Labfront Direct
                    </p>
                  </div>
                  {/* bar */}
                  <div
                    css={{
                      width: "100%",
                      height: calculateHeightOfBar(participantsLabfrontDirectConnected),
                      backgroundColor: color.surface.blue.brand,
                    }}
                  />
                </div>
                {participantNotSetupLabfrontDirect.length > 0 && (
                  <button
                    type="button"
                    id="not-setup-labfront-direct-button"
                    onClick={() =>
                      handleClick(
                        garminConnectEnabled
                          ? OverviewSidebarTab.HalfwaySetup
                          : OverviewSidebarTab.NotStartedYet,
                      )
                    }
                    css={{
                      width: "calc(100% - 8px)",
                      border: "none",
                      backgroundColor: "transparent",
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      padding: 4,
                      borderRadius: 4,
                      cursor: "pointer",
                      ":hover": {
                        backgroundColor: color.surface.hover.hoverWhiteBg,
                      },
                    }}
                  >
                    <span css={{ fontWeight: fontWeight.thick, color: color.textIcon.link }}>
                      {participantNotSetupLabfrontDirect.length}
                    </span>
                    <span
                      css={{
                        fontSize: fontSize.h8,
                        color: color.textIcon.secondary,
                      }}
                    >
                      {garminConnectEnabled
                        ? "Device setup halfway completed"
                        : "Device setup not started"}
                    </span>
                  </button>
                )}
              </div>,
            )}
          </div>
        </>
      </OverviewBlockTemplate>
    );
  };

  const TaskHeader = (props: { icon: React.ReactNode; title: string }) => {
    const { icon, title } = props;
    return (
      <div
        css={{
          display: "flex",
          padding: "8px",
          gap: "8px",
          width: "100%",
          borderRadius: "8px",
          background: color.surface.default,
          alignItems: "center",
        }}
      >
        {icon}
        <p
          css={{
            fontSize: fontSize.h7,
            fontWeight: fontWeight.bold,
            color: color.textIcon.secondary,
          }}
        >
          {title}
        </p>
      </div>
    );
  };

  // const TaskCard = (props: {item: ITask; index: number}) => {
  //   const {item, index} = props
  //   const taskContentName = getTaskContentName(item.type)
  //   const content = item[taskContentName]
  //   const sortedScheduleList = [...(item.scheduleList ?? [])]
  //     .filter((schedule) => schedule.current)
  //     .sort((a, b) => {
  //       return differenceInMilliseconds(new Date(b.createdAt), new Date(a.createdAt))
  //     })

  //   const taskTagStyle: React.CSSProperties = {
  //     border: `1px solid ${color.grey_160}`,
  //     borderRadius: '5px',
  //     borderLeft: item.color ? `6px solid #${item.color}` : `6px solid ${colorTranslator(index)}`,
  //     padding: '8px',
  //     gap: '8px',
  //     display: 'flex',
  //     flexDirection: 'column',
  //     alignItems: 'flex-start',
  //     width: '100%',
  //   }

  //   const eventTrackerTaskTagStyle: React.CSSProperties = {
  //     ...omit(taskTagStyle, 'borderLeft', 'border', 'background'),
  //     border: `1px solid #${item.color}`,
  //     background: setAlpha(`#${item.color}`, 0.08),
  //   }

  //   return (
  //     <div
  //       key={item?.id}
  //       css={{
  //         ...(item.eventTracker ? eventTrackerTaskTagStyle : taskTagStyle),
  //       }}
  //     >
  //       <div css={{display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-start'}}>
  //         <img
  //           width='20'
  //           height='20'
  //           src={
  //             item.type === TaskType.Todo
  //               ? ParticipantTaskIcon
  //               : item.type === TaskType.Timer
  //               ? TimerBlackIcon
  //               : item.type === TaskType.Questionnaire
  //               ? SurveyBlackIcon
  //               : item.type === TaskType.StopwatchGarminStream
  //               ? DeviceIcon
  //               : MovesenseImg
  //           }
  //           alt={`${item.type} icon`}
  //           css={{
  //             transform:
  //               item.type === TaskType.StopwatchMovesenseStream || item.type === TaskType.StopwatchMovesenseLogData
  //                 ? 'scale(1.2)'
  //                 : 'scale(1)',
  //           }}
  //         />
  //         {RIF(content?.name, (name) => (
  //           <span css={{fontSize: fontSize.h7, fontWeight: fontWeight.thick}}>{name}</span>
  //         ))}
  //       </div>
  //       {RIF(
  //         item.scheduleRepeat || item.scheduleDescription,
  //         <div css={{display: 'flex', alignItems: 'center', gap: '3px'}}>
  //           {RIF(item.scheduleRepeat, <img src={RepeatIcon} width='14' alt='Repeat icon' aria-hidden='true' />)}
  //           {RIF(item.scheduleDescription, (scheduleDescription) => (
  //             <span css={{fontSize: '12px', color: color.grey_400}}>{scheduleDescription}</span>
  //           ))}
  //         </div>,
  //       )}
  //       {RIF(
  //         item.type === TaskType.StopwatchMovesenseStream ||
  //           item.type === TaskType.StopwatchMovesenseLogData ||
  //           item.type === TaskType.StopwatchGarminStream,
  //         <div
  //           css={{
  //             background: color.background,
  //             borderRadius: '5px',
  //             padding: '4px 8px',
  //             color: color.grey_600,
  //             fontSize: fontSize.h8,
  //           }}
  //         >
  //           {RIF(
  //             item.type === TaskType.StopwatchMovesenseStream || item.type === TaskType.StopwatchMovesenseLogData,
  //             convertMovesenseDescription(content as IStopwatchMovesenseStream),
  //           )}
  //           {RIF(
  //             item.type === TaskType.StopwatchGarminStream,
  //             convertGarminStreamDescription(content as IStopwatchGarminStream),
  //           )}
  //         </div>,
  //       )}
  //       {RIF(
  //         sortedScheduleList.length,
  //         <div
  //           css={{
  //             width: '100%',
  //             display: 'flex',
  //             flexDirection: 'column',
  //             gap: '8px',
  //           }}
  //         >
  //           {sortedScheduleList.map((schedule) => (
  //             <TaskScheduleCard
  //               key={schedule.id}
  //               schedule={schedule}
  //               isViewOnly={true}
  //               style={{
  //                 border: `1px solid ${color.border.default}`,
  //                 borderRadius: '5px',
  //                 cursor: 'default',
  //               }}
  //             />
  //           ))}
  //         </div>,
  //       )}
  //       {item.reminderList?.map((reminder, index) => (
  //         <div
  //           key={index}
  //           css={{
  //             background: color.background,
  //             borderRadius: '5px',
  //             padding: '4px 8px',
  //             display: 'flex',
  //             alignItems: 'center',
  //             gap: '8px',
  //           }}
  //         >
  //           <img src={BellIcon} width='16' alt='Reminder icon' aria-hidden='true' />
  //           {RIF(generateReminderString(reminder), (reminderString) => (
  //             <p css={{color: color.textIcon.secondary, fontSize: fontSize.h8}}>{reminderString}</p>
  //           ))}
  //         </div>
  //       ))}
  //     </div>
  //   )
  // }

  const TaskOverview = () => {
    const { eventTrackerTaskList, scheduleTaskList, legacyTaskList } = splitTasks(taskList);
    return (
      <OverviewBlockTemplate>
        <>
          <p
            css={{
              fontWeight: fontWeight.thick,
            }}
          >{`Total ${taskList.filter((t) => t.type !== TaskType.GarminDevice).length} Tasks`}</p>
          <>
            {RIF(
              eventTrackerTaskList.length,
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <TaskHeader
                  icon={<EventIcon fill={color.textIcon.secondary} />}
                  title="Event Tracking Task"
                />
                {eventTrackerTaskList.map((item, index) => (
                  <TaskCard item={item} index={index} />
                ))}
              </div>,
            )}
            {RIF(
              scheduleTaskList.length,
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <TaskHeader
                  icon={<CalendarIcon fill={color.textIcon.secondary} />}
                  title="Scheduled Task"
                />
                {scheduleTaskList.map((item, index) => (
                  <TaskCard item={item} index={index} />
                ))}
              </div>,
            )}
            {RIF(
              legacyTaskList.length,
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {legacyTaskList.map((item, index) => (
                  <TaskCard item={item} index={index} />
                ))}
              </div>,
            )}
          </>
        </>
      </OverviewBlockTemplate>
    );
  };

  const AttentionOverview = () => {
    const [displayNotificationInfoPop, setDisplayNotificationInfoPop] = useState<boolean>(false);
    const [displayNotificationDiscardPop, setDisplayNotificationDiscardPop] =
      useState<boolean>(false);
    const [notificationInfo, setNotificationInfo] = useState<{
      message: { msgTitle: string; msgContent: string };
      response: unknown;
    }>({
      message: {
        msgTitle: "",
        msgContent: "",
      },
      response: {},
    });

    const colorMap = {
      green: { txtColor: color.tag.green.dark, bgColor: color.tag.green.light },
      yellow: { txtColor: color.tag.yellow.dark, bgColor: color.tag.yellow.light },
      blue: { txtColor: color.tag.blue.dark, bgColor: color.tag.blue.light },
      purple: { txtColor: color.tag.purple.dark, bgColor: color.tag.purple.light },
      red: { txtColor: color.tag.red.dark, bgColor: color.tag.red.light },
      grey: { txtColor: color.tag.grey.dark, bgColor: color.tag.grey.light },
    };

    const issueMetadataMap: {
      [issue: string]: {
        issueCode: string;
        description: string;
        type: "garminDirect" | "garminConnect" | "device";
        colorTheme: keyof typeof colorMap;
      };
    } = {
      notOpenAppSinceYesterday: {
        issueCode: "Issue A",
        description:
          "Participants haven’t opened the Labfront Companion app since yesterday. Please remind them to open it regularly.",
        type: "garminDirect",
        colorTheme: "green",
      },
      notOpenApp2Days: {
        issueCode: "Issue A",
        description:
          "Participants haven’t opened the Labfront Companion app since yesterday. Please remind them to open it regularly.",
        type: "garminDirect",
        colorTheme: "green",
      },
      notCompleteSyncGarminDeviceSinceYesterday: {
        issueCode: "Issue B",
        description:
          "Participants haven’t synced with the Labfront Companion app since yesterday. Please remind them to keep the app open until syncing completes.",
        type: "garminDirect",
        colorTheme: "yellow",
      },
      notCompleteSyncGarminDevice2Days: {
        issueCode: "Issue B",
        description:
          "Participants haven't synced with the Labfront Companion app in the past 2 days. Please remind them to keep the app open until syncing completes.",
        type: "garminDirect",
        colorTheme: "yellow",
      },
      notOpenApp3Days: {
        issueCode: "Issue A",
        description:
          "Participants haven't opened the Labfront Companion app in the past 3 days. Please remind them to open it regularly.",
        type: "garminDirect",
        colorTheme: "green",
      },
      notCompleteSyncGarminDevice3Days: {
        issueCode: "Issue B",
        description:
          "Participants haven't synced with the Labfront Companion app in the past 3 days. Please remind them to keep the app open until syncing completes.",
        type: "garminDirect",
        colorTheme: "yellow",
      },
      garminDirectBbi3DaysAvgLowerThen4Hours: {
        issueCode: "Issue C",
        description:
          "Participants have little or no BBI data for the past 3 days. Please remind them to wear their device consistently.",
        type: "garminDirect",
        colorTheme: "blue",
      },
      garminDirectBbiNotEnoughInYesterday: {
        issueCode: "Issue C",
        description:
          "Participants wore their device for less than 10 hours yesterday. Please remind them to wear it consistently throughout the day.",
        type: "garminDirect",
        colorTheme: "blue",
      },
      garminDirectBbiNotEnoughInPrevious3Days: {
        issueCode: "Issue C",
        description:
          "Participants have little or no BBI data for the past 3 days. Please remind them to wear their device consistently.",
        type: "garminDirect",
        colorTheme: "blue",
      },
      garminConnectHr3DaysAvgLowerThen4Hours: {
        issueCode: "Issue D",
        description:
          "Participants have little or no heart rate data from the Garmin Connect app for the past 3 days. Please remind them to wear their device consistently.",
        type: "garminConnect",
        colorTheme: "purple",
      },
      garminConnectHrNotEnoughInPrevious3Days: {
        issueCode: "Issue D",
        description:
          "Participants have little or no heart rate data from the Garmin Connect app for the past 3 days. Please remind them to wear their device consistently.",
        type: "garminConnect",
        colorTheme: "purple",
      },
      garminDirectRecentStepAndBbiInconsistent: {
        issueCode: "Issue E",
        description:
          "Participants may be experiencing an issue with the wristband's heart rate sensor. Please ask them to restart the device.",
        type: "device",
        colorTheme: "red",
      },
      notSyncGarminConnect3Days: {
        issueCode: "Issue F",
        description:
          "Participants have no data from Garmin Connect app in the past 3 days. Please ask them to open the app. If the issue persists, contact Labfront Support.",
        type: "garminConnect",
        colorTheme: "grey",
      },
      notSyncGarminConnect2Days: {
        issueCode: "Issue F",
        description:
          "Participants have no data from Garmin Connect app in the past 2 days. Please ask them to open the app. If the issue persists, contact Labfront Support.",
        type: "garminConnect",
        colorTheme: "grey",
      },
      garminConnectLinkDuplicated: {
        issueCode: "Issue G",
        description:
          "Participants are using the same GC account. This may result in unusable data.",
        type: "garminConnect",
        colorTheme: "red",
      },
    };

    const issueOverviewData = useMemo(() => {
      if (participantIssue.issuesIndexMap && participantIssue.participantIssuesMap) {
        return Object.entries(participantIssue.issuesIndexMap)
          .filter(([, issue]) => participantIssueList.includes(issue))
          .map(([index, issue]) => {
            const participantCount = Object.values(participantIssue.participantIssuesMap).filter(
              (participantIssueList) =>
                participantIssueList.issues.some((i) => i === parseInt(index)),
            ).length;
            return {
              index,
              issue,
              participantCount,
            };
          })
          .filter((d) => d.participantCount > 0)
          .sort((a, b) => {
            if (a.issue === "garminConnectLinkDuplicated") return -1;
            if (b.issue === "garminConnectLinkDuplicated") return 1;
            return 0;
          });
      }
      return [];
    }, [participantIssue]);

    const handleRenderFullList = () => {
      setRenderFullList(true);
    };

    const handleCloseFullList = () => {
      setRenderFullList(false);
    };

    const lastUpdatedTime = useMemo(() => {
      const d = participantIssue.updatedDate;
      const hh = d.getHours().toString().padStart(2, "0");
      const mm = d.getMinutes().toString().padStart(2, "0");
      return `${hh}:${mm}`;
    }, [participantIssue]);

    const AttentionOverviewEntry = (props: {
      type: "garminConnect" | "garminDirect" | "device";
      participantCount: number;
      description: string;
      isGarminAccountIssue: boolean;
    }) => {
      const { type, participantCount, description, isGarminAccountIssue } = props;
      const iconSrc = useMemo(() => {
        switch (type) {
          case "garminConnect":
            return GarminConnectIcon;
          case "garminDirect":
            return LabfrontIcon;
          case "device":
            return DeviceIcon;
        }
      }, [type]);

      return (
        <div
          css={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <img
            width={20}
            height={20}
            src={iconSrc}
            alt={`${
              type === "garminConnect"
                ? "Garmin Connect"
                : type === "garminDirect"
                  ? "Labfront Direct"
                  : "Device"
            } icon`}
          />
          <span
            css={{
              fontSize: fontSize.h4,
              fontWeight: fontWeight.thick,
              width: 40,
              minWidth: 40,
              textAlign: "center",
            }}
          >
            {participantCount}
          </span>
          <div
            css={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isGarminAccountIssue && (
              <span
                css={{
                  fontSize: fontSize.h7,
                  fontWeight: fontWeight.thick,
                  color: color.textIcon.warning,
                }}
              >
                ⚠️ Garmin Account Issue
              </span>
            )}
            <span css={{ fontSize: fontSize.h7, color: color.textIcon.secondary }}>
              {description}
            </span>
          </div>
        </div>
      );
    };

    const FullAttentionList = (props: {
      participantList: IParticipant[];
      handleClosePopup: () => void;
    }) => {
      const { participantList, handleClosePopup } = props;
      const participantWithIssues = useMemo(() => {
        const garminConnectDuplicatedParticipatnIdList =
          participantIssue?.garminConnectLinkDuplicatedParticiipantSetList?.flat() ?? [];

        return participantList.filter((p) => {
          if (garminConnectDuplicatedParticipatnIdList.length > 0) {
            if (garminConnectDuplicatedParticipatnIdList.includes(p.id)) {
              return true;
            }
          }

          return (
            p.activated &&
            participantIssue.participantIssuesMap &&
            participantIssue.participantIssuesMap[p.id] &&
            participantIssue.participantIssuesMap[p.id].issues.length > 0
          );
        });
      }, [participantIssue, participantList]);

      const selectAll = useMemo(() => {
        if (filterByIssue === null) {
          return selectedParticipantList.length === participantWithIssues.length;
        } else {
          return (
            selectedParticipantList.length ===
            participantWithIssues.filter((p) =>
              participantIssue.participantIssuesMap[p.id]?.issues.includes(filterByIssue),
            ).length
          );
        }
      }, [filterByIssue, participantWithIssues, selectedParticipantList]);

      const handleSubmit = () => {
        setSendNotificationParticipantIdList(selectedParticipantList);
        setDisplayPopupSendNotification(true);
      };

      const handleSendNotificationToSingleParticipant = (pid: string) => {
        setSendNotificationParticipantIdList([pid]);
        setDisplayPopupSendNotification(true);
      };

      const handleSelectAll = () => {
        if (selectAll) {
          setSelectedParticipantList([]);
        } else {
          if (filterByIssue === null) {
            setSelectedParticipantList(participantWithIssues.map((p) => p.id));
          } else {
            setSelectedParticipantList(
              participantWithIssues
                .filter((p) =>
                  participantIssue.participantIssuesMap[p.id]?.issues.includes(filterByIssue),
                )
                .map((p) => p.id),
            );
          }
        }
      };

      const handleSelectGroup = (issueIndex: string) => {
        setSelectedParticipantList(
          selectedParticipantList.filter((pid) =>
            participantIssue.participantIssuesMap[pid]?.issues.includes(parseInt(issueIndex)),
          ),
        );
        setFilterByIssue(parseInt(issueIndex) === filterByIssue ? null : parseInt(issueIndex));
      };

      const Tag = (props: { children: string; colorTheme: keyof typeof colorMap }) => {
        const { children, colorTheme } = props;
        return (
          <span
            css={{
              // alignSelf: 'flex-start',
              padding: "2px 6px",
              borderRadius: 2,
              backgroundColor: colorMap[colorTheme].bgColor,
              color: colorMap[colorTheme].txtColor,
              fontSize: fontSize.h8,
              fontWeight: fontWeight.thick,
              whiteSpace: "nowrap",
            }}
          >
            {children}
          </span>
        );
      };

      const Entry = (props: { participant: IParticipant; isLast: boolean }) => {
        const { participant, isLast } = props;
        const issueList = participantIssue.participantIssuesMap[participant.id].issues
          .filter((issueIndex) =>
            participantIssueList.includes(participantIssue.issuesIndexMap[issueIndex]),
          )
          .map((index) => participantIssue.issuesIndexMap[index]);
        const handleCheckBoxOnChange = () => {
          const index = selectedParticipantList.findIndex((pid) => pid === participant.id);
          if (index === -1) {
            setSelectedParticipantList([...selectedParticipantList, participant.id]);
          } else {
            setSelectedParticipantList((prev) => [
              ...prev.slice(0, index),
              ...prev.slice(index + 1),
            ]);
          }
        };
        return (
          <div
            css={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: "8px 16px",
              borderBottom: isLast ? "none" : `1px solid ${color.border.light}`,
            }}
          >
            <Checkbox
              value={selectedParticipantList.some((pid) => pid === participant.id)}
              onChange={handleCheckBoxOnChange}
            />
            <span
              css={{
                width: 144,
                fontSize: fontSize.h7,
              }}
            >
              {participant.insignia}
            </span>
            <button
              type="button"
              onClick={() => handleSendNotificationToSingleParticipant(participant.id)}
              aria-label="Send notification to this participant"
              css={{
                width: 16,
                height: 16,
                backgroundColor: "transparent",
                border: "none",
                borderRadius: 5,
                backgroundImage: `url(${NotificationBlackIcon})`,
                backgroundSize: "contain",
                backgroundPosition: "center",
                cursor: "pointer",
                ":hover": {
                  backgroundColor: color.surface.hover.hoverWhiteBg,
                },
              }}
            />
            <div
              css={{
                flex: 1,
                paddingLeft: 16,
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
              }}
            >
              {issueList.map((issue) => {
                const { colorTheme, issueCode } = issueMetadataMap[issue];
                return (
                  <Tag key={issue} {...{ colorTheme }}>
                    {issue === "garminConnectLinkDuplicated"
                      ? `⚠️ Garmin Account Issue: Case ${
                          (participantIssue.garminConnectLinkDuplicatedParticiipantSetList?.findIndex(
                            (v) => v.includes(participant.id),
                          ) ?? 0) + 1
                        }`
                      : issueCode}
                  </Tag>
                );
              })}
            </div>
          </div>
        );
      };

      const FilterButton = (props: {
        issueIndex: string;
        issueCode: string;
        colorTheme: keyof typeof colorMap;
        isGarminAccountIssue: boolean;
      }) => {
        const { issueIndex, issueCode, colorTheme, isGarminAccountIssue } = props;

        return (
          <button
            type="button"
            css={{
              height: 40,
              backgroundColor: color.surface.white,
              border: `1px solid ${parseInt(issueIndex) === filterByIssue ? color.border.black : color.border.light}`,
              borderRadius: 999,
              padding: "0 16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              ":hover": {
                backgroundColor: color.surface.hover.hoverWhiteBg,
              },
            }}
            onClick={() => handleSelectGroup(issueIndex)}
          >
            <Tag {...{ colorTheme }}>
              {isGarminAccountIssue ? "⚠️ Garmin Account Issue" : issueCode}
            </Tag>
            <span css={{ fontSize: fontSize.h7, color: color.textIcon.secondary }}>{`(${
              participantWithIssues.filter((p) => {
                return participantIssue.participantIssuesMap[p.id].issues.includes(
                  parseInt(issueIndex),
                );
              }).length
            })`}</span>
            {parseInt(issueIndex) === filterByIssue && (
              <img
                src={CheckBlackIcon}
                width={16}
                height={16}
                alt="Selected icon"
                aria-hidden="true"
              />
            )}
          </button>
        );
      };

      const IssueDescription = (props: {
        issueIndex: string;
        issueCode: string;
        colorTheme: keyof typeof colorMap;
        description: string;
        isGarminAccountIssue: boolean;
      }) => {
        const { issueIndex, issueCode, colorTheme, description, isGarminAccountIssue } = props;

        return (
          <div
            // onClick={() => handleSelectGroup(issueIndex)}
            css={{
              alignSelf: "flex-start",
              padding: 5,
              borderRadius: 5,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              // cursor: 'pointer',
              // border: 'none',
              // backgroundColor: parseInt(issueIndex) === filterByIssue ? color.surface.hover.hoverGreyBg : 'transparent',
              // ':hover': {
              //   backgroundColor: color.surface.hover.hoverGreyBg,
              // },
            }}
          >
            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Tag {...{ colorTheme }}>
                {isGarminAccountIssue ? "⚠️ Garmin Account Issue" : issueCode}
              </Tag>
              <span css={{ fontSize: fontSize.h8, color: color.textIcon.secondary }}>
                {
                  participantWithIssues.filter((p) => {
                    return participantIssue.participantIssuesMap[p.id].issues.includes(
                      parseInt(issueIndex),
                    );
                  }).length
                }
                &nbsp;participant(s)
              </span>
            </div>
            {!isGarminAccountIssue && (
              <p css={{ fontSize: fontSize.h8, textAlign: "start" }}>{description}</p>
            )}
            {isGarminAccountIssue && (
              <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <p
                  css={{
                    fontSize: fontSize.h8,
                  }}
                >
                  We detected participants using the same Garmin Connect (GC) account (this can
                  occur when keying in the wrong account). This will result in the participant data
                  being averaged and may become unusable.
                </p>
                <p
                  css={{
                    fontSize: fontSize.h8,
                  }}
                >
                  Each “Case” is marking a set of participants using the same GC account. ie: If you
                  have 3 participants using the same GC account, you'll have 3 participants with
                  “Case 1”.
                </p>
                <p
                  css={{
                    fontSize: fontSize.h8,
                  }}
                >
                  If you see this issue, please contact support@labfront.com
                </p>
              </div>
            )}
          </div>
        );
      };

      const filteredParticipantList = participantWithIssues.filter((p) => {
        if (filterByIssue === null) return true;
        return participantIssue.participantIssuesMap[p.id].issues.includes(filterByIssue);
      });

      return (
        <>
          <PopupTemplate
            {...{
              title: "Participants Needing Follow-Up",
              description:
                "Below is a list of participants who may need attention based on the issue keys shown on the right. Please select the participant IDs you wish to message.",
              background: true,
              popupSize: PopupSize.Xl,
              handleClosePopup,
              mainFunction: handleSubmit,
              buttonContent: "Send Notification To Selected Participants",
              buttonDisabled: selectedParticipantList.length === 0,
              optionalBottomLeftContent: (
                <CopyParticipantListButton {...{ filteredParticipantList, color }} />
              ),
            }}
          >
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              {/* filter */}
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {issueOverviewData.map(({ index, issue }) => {
                  const { issueCode, colorTheme } = issueMetadataMap[issue];
                  return (
                    <FilterButton
                      key={issue}
                      {...{
                        issueIndex: index,
                        issueCode,
                        colorTheme,
                        isGarminAccountIssue: issue === "garminConnectLinkDuplicated",
                      }}
                    />
                  );
                })}
              </div>
              <div
                css={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "77fr 34fr",
                  gap: 24,
                }}
              >
                {/* participant table */}
                <div
                  css={{
                    width: 616,
                    borderRadius: 5,
                    border: `1px solid ${color.border.light}`,
                    alignSelf: "flex-start",
                  }}
                >
                  {/* header */}
                  <div
                    css={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      padding: "8px 16px",
                      backgroundColor: color.surface.default,
                      borderTopLeftRadius: 5,
                      borderTopRightRadius: 5,
                      borderBottom: `1px solid ${color.border.light}`,
                    }}
                  >
                    <Checkbox
                      {...{
                        onChange: handleSelectAll,
                        value: selectAll,
                      }}
                    />
                    <span
                      css={{
                        width: 144,
                        fontSize: fontSize.h7,
                      }}
                    >
                      Participant ID
                    </span>
                    <img width={16} src={NotificationBlackIcon} alt="Notification icon" />
                    <span
                      css={{
                        flex: 1,
                        paddingLeft: 16,
                        fontSize: fontSize.h7,
                      }}
                    >
                      Issue
                    </span>
                  </div>
                  {filteredParticipantList.map((participant, i) => (
                    <Entry
                      key={participant.id}
                      {...{
                        participant,
                        isLast: i === participantWithIssues.length - 1,
                      }}
                    />
                  ))}
                </div>
                <div
                  css={{
                    padding: 16,
                    borderRadius: 8,
                    backgroundColor: color.surface.default,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <span
                    css={{
                      fontSize: fontSize.h7,
                      fontWeight: fontWeight.bold,
                      color: color.textIcon.secondary,
                    }}
                  >
                    Issue Keys
                  </span>
                  <div
                    css={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 24,
                    }}
                  >
                    {issueOverviewData.map(({ index, issue }) => {
                      const { issueCode, colorTheme, description } = issueMetadataMap[issue];
                      return (
                        <IssueDescription
                          key={issue}
                          {...{
                            issueIndex: index,
                            issueCode,
                            colorTheme,
                            description,
                            isGarminAccountIssue: issue === "garminConnectLinkDuplicated",
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* {CopyDynamic('Copy the participant list' ,filteredParticipantList.map((p) => p.insignia).join(', '))} */}
            </div>
          </PopupTemplate>
        </>
      );
    };

    return (
      <>
        {renderFullList && participantList && (
          <FullAttentionList
            {...{
              participantList,
              handleClosePopup: handleCloseFullList,
            }}
          />
        )}

        {displayPopupSendNotification && (
          <PopupSendNotification
            {...{
              sendNotificationParticipantIdList,
              setNotificationInfo,
              setDisplayPopupSendNotification,
              setDisplayNotificationDiscardPop,
              setDisplayNotificationInfoPop,
              zIndex: 105,
            }}
          />
        )}

        {displayNotificationDiscardPop && (
          <ParticipantNotificationDiscardPop
            {...{
              setDisplayNotificationDiscardPop,
              setDisplayPopupSendNotification,
              zIndex: 105,
            }}
          />
        )}

        {displayNotificationInfoPop && (
          <ParticipantNotificationInfoPop
            {...{
              notificationList: participantList.filter((p) =>
                sendNotificationParticipantIdList.some((pid) => pid === p.id),
              ),
              notificationInfo,
              closeAction: setDisplayNotificationInfoPop,
              setRenderFullList,
              zIndex: 105,
            }}
          />
        )}

        <div
          css={{
            width: "100%",
            padding: 16,
            borderRadius: 5,
            border: `1px solid ${color.border.warning}`,
            backgroundColor: color.surface.orange.light,
            boxShadow: "0px 4px 12px 0px rgba(212, 212, 212, 0.25)",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* header */}
          <div
            css={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <p css={{ fontWeight: fontWeight.thick }}>Participants Needing Follow-Up</p>
              <p css={{ fontSize: fontSize.h7, color: color.textIcon.secondary }}>
                Last updated at {lastUpdatedTime} Today
              </p>
            </div>
            <ButtonReverse
              onClick={handleRenderFullList}
              {...{
                btnPadding: "small",
                bgColor: "transparent",
                css: { height: 24 },
                fontWeight: fontWeight.bold,
              }}
            >
              View Full List
            </ButtonReverse>
          </div>
          {/* content */}
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: 24,
            }}
          >
            {issueOverviewData.map(({ index, issue, participantCount }) => {
              const issueMetadata = issueMetadataMap[issue];
              if (issueMetadata) {
                const { type, description } = issueMetadata;
                return (
                  <AttentionOverviewEntry
                    key={issue}
                    {...{
                      type,
                      participantCount,
                      description,
                      isGarminAccountIssue: issue === "garminConnectLinkDuplicated",
                    }}
                  />
                );
              }
            })}
          </div>
        </div>
      </>
    );
  };

  const OverviewHeatmap = (props: {
    type: "garminConnect" | "garminDirect";
    dailyStatus: DailyStatus;
    heatmapData: HeatmapData;
    empty?: boolean;
  }) => {
    const { type, dailyStatus, heatmapData, empty } = props;
    const navigate = useNavigate();

    const handleViewFullHeatmap = () => {
      if (type === "garminConnect") {
        navigate(`/adherence/${projectId}/garminConnectWearTime`);
      } else if (type === "garminDirect") {
        navigate(`/adherence/${projectId}/garminDirectBbiDigest`);
      }
    };

    const handleClickDailyQualityCell = (yymmdd: number, dataType: keyof DailyStatusItem) => {
      setSelectedCell(null);
      if (dailyStatus[yymmdd][dataType].length === 0) return setExpandSidebar(false);
      setSelectedCell(`${type}-${yymmdd}-${dataType}`);
      setExpandSidebar(true);
      setSidebarDatatype("heatmap");
      setSidebarDate(yymmdd);
      setSidebarDefaultTab(OverviewSidebarTab.InCell);
      setSidebarDeviceType(type);
      setParticipantInCell(dailyStatus[yymmdd][dataType]);
      switch (dataType) {
        case "gt4":
          return setSidebarInCellTabTitle(OverviewSidebarInCellTabTitle.Gt4);
        case "lt4":
          return setSidebarInCellTabTitle(OverviewSidebarInCellTabTitle.Lt4);
        case "noData":
          return setSidebarInCellTabTitle(OverviewSidebarInCellTabTitle.NoData);
        case "notSynced":
          return setSidebarInCellTabTitle(OverviewSidebarInCellTabTitle.NotSyncedYet);
      }
    };

    const HeatmapCell = (props: {
      id: string;
      type: "garminConnect" | "garminDirect";
      wearTime?: number;
      activated?: boolean;
    }) => {
      const { id, type, wearTime, activated } = props;
      const { wearTimeHours, wearTimeMinutes } = useMemo(() => {
        if (wearTime === undefined) return { wearTimeHours: undefined, wearTimeMinutes: undefined };
        const wearTimeInMinutes = type === "garminConnect" ? wearTime : wearTime / 1000 / 60;
        return {
          wearTimeHours: Math.floor(wearTimeInMinutes / 60),
          wearTimeMinutes: Math.ceil(wearTimeInMinutes % 60),
        };
      }, [wearTime, type]);

      const wearTimeColor = () => {
        if (wearTimeHours !== undefined) {
          if (wearTimeHours <= 8) {
            return type === "garminConnect" ? color.heatmap.gc._1 : color.heatmap.lf._1;
          } else if (wearTimeHours <= 12) {
            return type === "garminConnect" ? color.heatmap.gc._2 : color.heatmap.lf._2;
          } else if (wearTimeHours <= 16) {
            return type === "garminConnect" ? color.heatmap.gc._3 : color.heatmap.lf._3;
          } else if (wearTimeHours <= 20) {
            return type === "garminConnect" ? color.heatmap.gc._4 : color.heatmap.lf._4;
          } else {
            return type === "garminConnect" ? color.heatmap.gc._5 : color.heatmap.lf._5;
          }
        }
      };

      const tooltipText = useMemo(() => {
        if (!activated) return "Not joined yet";
        if (wearTimeHours === undefined) return "Not synced yet";
        if (wearTime === 0) return `No ${type === "garminConnect" ? "HR" : "BBI"} data`;
        return `${wearTimeHours} hr${wearTimeMinutes > 0 ? ` ${wearTimeMinutes} min` : ""}`;
      }, [wearTimeHours, wearTimeMinutes, type, activated]);

      const Content = () => {
        if (!activated) {
          return (
            <div
              css={{
                height: "100%",
                borderRadius: 4,
                cursor: "pointer",
                backgroundImage: `url(${NotJoinedBG})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            />
          );
        }
        if (wearTimeHours === undefined) {
          return (
            <div
              css={{
                height: "100%",
                borderRadius: 4,
                backgroundColor: color.surface.default,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundImage: `url(${NotSyncedIcon})`,
                backgroundSize: 12,
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            />
          );
        } else {
          if (wearTime === 0) {
            return (
              <div
                css={{
                  height: "100%",
                  borderRadius: 4,
                  border: `1px solid ${color.border.light}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <span
                  css={{
                    fontSize: fontSize.h8,
                    color: color.textIcon.secondary,
                    pointerEvents: "none",
                  }}
                >
                  {`No ${type === "garminConnect" ? "HR" : "BBI"}`}
                </span>
              </div>
            );
          } else if (wearTimeHours <= 4) {
            return (
              <div
                css={{
                  height: "100%",
                  borderRadius: 4,
                  backgroundImage: `url(${HeatmapLowWearTimeBG})`,
                  cursor: "pointer",
                }}
              />
            );
          } else {
            return (
              <div
                css={{
                  height: "100%",
                  borderRadius: 4,
                  backgroundColor: wearTimeColor(),
                  cursor: "pointer",
                }}
              />
            );
          }
        }
      };

      return (
        <div
          onMouseEnter={() => setHoveredCell(id)}
          onMouseLeave={() => setHoveredCell(null)}
          css={{
            position: "relative",
          }}
        >
          {hoveredCell === id && (
            <div
              css={{
                padding: "8px 12px",
                borderRadius: 4,
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: 20,
                backgroundColor: color.black,
                boxShadow: "0 4px 10px 1px rgba(104, 104, 104, 0.25)",
                color: color.white,
                fontSize: fontSize.h7,
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              {tooltipText}
              <img
                height={13}
                css={{
                  position: "absolute",
                  top: 28,
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
                src={BlackTriangleIcon}
                width="16"
                alt="Tooltip triangle icon"
                aria-hidden="true"
              />
            </div>
          )}
          <Content />
        </div>
      );
    };

    return (
      <div onMouseLeave={() => setHoveredCell(null)}>
        <OverviewBlockTemplate>
          <>
            {/* title */}
            <div
              css={{
                width: "100%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <img
                  src={type === "garminConnect" ? GarminConnectIcon : LabfrontIcon}
                  width="24"
                  alt={`${type === "garminConnect" ? "Garmin Connect" : "Labfront Direct"} icon`}
                  aria-hidden="true"
                />
                <p css={{ fontWeight: fontWeight.thick }}>
                  {type === "garminConnect"
                    ? "Heart Rate (Garmin Connect app)"
                    : "BBI (Labfront Direct app)"}
                </p>
                <Tooltip
                  content={
                    type === "garminConnect"
                      ? "This card shows the heart rate data collected using the Garmin Connect app."
                      : "This card shows the beat-to-beat interval heart rate data collected using the Labfront Companion app."
                  }
                />
              </div>
              <ButtonReverse
                onClick={handleViewFullHeatmap}
                {...{
                  btnColor: "grey_600",
                  btnPadding: "small",
                  fontSize: fontSize.h7,
                  fontWeight: fontWeight.bold,
                }}
              >
                View Full Heatmap
              </ButtonReverse>
            </div>
            {empty && (
              <div
                css={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: "57px 40px",
                }}
              >
                <p
                  css={{
                    width: "100%",
                    textAlign: "center",
                    color: color.textIcon.hint,
                    fontWeight: fontWeight.bold,
                  }}
                >
                  No data collection yet
                </p>
                <p
                  css={{
                    width: "100%",
                    textAlign: "center",
                    color: color.textIcon.hint,
                    fontSize: fontSize.h7,
                  }}
                >
                  Once participants install the app and begin syncing their wearable devices, their
                  data collection status will show here.
                </p>
              </div>
            )}
            {!empty && (
              <>
                {/* Daily Data Quality Summary */}
                <div
                  css={{
                    display: "grid",
                    gridTemplateColumns: "17fr 8fr 8fr 8fr 8fr 8fr 8fr 8fr",
                    width: "100%",
                    height: 160,
                    borderBottom: `1px solid ${color.border.light}`,
                    boxSizing: "border-box",
                  }}
                >
                  {/* header */}
                  <div
                    css={{
                      height: "100%",
                      borderRight: `1px solid ${color.border.light}`,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <span
                      css={{
                        width: "100%",
                        height: 64,
                        borderBottom: `1px solid ${color.border.light}`,
                        backgroundColor: color.surface.default,
                        padding: 16,
                        display: "flex",
                        alignItems: "center",
                        fontSize: fontSize.h7,
                        color: color.textIcon.secondary,
                      }}
                    >
                      Date
                    </span>
                    <span
                      css={{
                        width: "100%",
                        height: 24,
                        padding: "4px 16px",
                        fontSize: fontSize.h7,
                        color: color.textIcon.secondary,
                      }}
                    >
                      Data &gt; 4hrs
                    </span>
                    <span
                      css={{
                        width: "100%",
                        height: 24,
                        padding: "4px 16px",
                        fontSize: fontSize.h7,
                        color: color.textIcon.secondary,
                      }}
                    >
                      Data &lt; 4hrs
                    </span>
                    <span
                      css={{
                        width: "100%",
                        height: 24,
                        padding: "4px 16px",
                        fontSize: fontSize.h7,
                        color: color.textIcon.secondary,
                      }}
                    >
                      No {type === "garminConnect" ? "HR" : "BBI"} data
                    </span>
                    <span
                      css={{
                        width: "100%",
                        height: 24,
                        padding: "4px 16px",
                        fontSize: fontSize.h7,
                        color: color.textIcon.secondary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Haven't sync...
                    </span>
                  </div>
                  {/* column for one week */}
                  {yymmddIndexList.map((yymmdd) => {
                    const [month, day] = formatYymmddToLabel(yymmdd);
                    const dailyDataQualityStatus = dailyStatus[yymmdd] ?? {
                      gt4: [],
                      lt4: [],
                      noData: [],
                      notSynced: participantList.map((p) => p.id),
                    };
                    return (
                      <div
                        key={yymmdd}
                        css={{
                          height: "100%",
                          borderRight: `1px solid ${color.border.light}`,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* date */}
                        <div
                          css={{
                            width: "100%",
                            height: 64,
                            borderBottom: `1px solid ${color.border.light}`,
                            backgroundColor: color.surface.default,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <span
                            css={{
                              fontSize: fontSize.h8,
                              fontWeight: fontWeight.thick,
                              color: color.textIcon.secondary,
                            }}
                          >
                            {month}
                          </span>
                          <span
                            css={{
                              fontWeight: fontWeight.bold,
                            }}
                          >
                            {day}
                          </span>
                        </div>
                        {/* data > 4hrs */}
                        <button
                          type="button"
                          id={`daily-quality-cell-${type}-${yymmdd}-gt4`}
                          onClick={() => handleClickDailyQualityCell(yymmdd, "gt4")}
                          aria-label={`${dailyDataQualityStatus.gt4.length} participants with more than 4 hours data for this day`}
                          css={{
                            height: 24,
                            border:
                              selectedCell === `${type}-${yymmdd}-gt4`
                                ? `2px solid ${color.border.black}`
                                : "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: fontSize.h7,
                            fontWeight: fontWeight.bold,
                            backgroundColor:
                              dailyDataQualityStatus.gt4.length > 0
                                ? color.tag.green.light
                                : "transparent",
                            cursor: "pointer",
                            ":hover": {
                              border: `2px solid ${color.border.black}`,
                            },
                          }}
                        >
                          {dailyDataQualityStatus.gt4.length}
                        </button>
                        {/* data < 4hrs */}
                        <button
                          type="button"
                          id={`daily-quality-cell-${type}-${yymmdd}-lt4`}
                          onClick={() => handleClickDailyQualityCell(yymmdd, "lt4")}
                          aria-label={`${dailyDataQualityStatus.lt4.length} participants with less than 4 hours data for this day`}
                          css={{
                            height: 24,
                            border:
                              selectedCell === `${type}-${yymmdd}-lt4`
                                ? `2px solid ${color.border.black}`
                                : "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: fontSize.h7,
                            fontWeight: fontWeight.bold,
                            backgroundColor:
                              dailyDataQualityStatus.lt4.length > 0
                                ? color.tag.blue.light
                                : "transparent",
                            cursor: "pointer",
                            ":hover": {
                              border: `2px solid ${color.border.black}`,
                            },
                          }}
                        >
                          {dailyDataQualityStatus.lt4.length}
                        </button>
                        {/* no hr data */}
                        <button
                          type="button"
                          id={`daily-quality-cell-${type}-${yymmdd}-noData`}
                          onClick={() => handleClickDailyQualityCell(yymmdd, "noData")}
                          aria-label={`${dailyDataQualityStatus.noData.length} participants with no data for this day`}
                          css={{
                            height: 24,
                            border:
                              selectedCell === `${type}-${yymmdd}-noData`
                                ? `2px solid ${color.border.black}`
                                : "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: fontSize.h7,
                            fontWeight: fontWeight.bold,
                            backgroundColor:
                              dailyDataQualityStatus.noData.length > 0
                                ? color.tag.orange.light
                                : "transparent",
                            cursor: "pointer",
                            ":hover": {
                              border: `2px solid ${color.border.black}`,
                            },
                          }}
                        >
                          {dailyDataQualityStatus.noData.length}
                        </button>
                        {/* haven't synced yet */}
                        <button
                          type="button"
                          id={`daily-quality-cell-${type}-${yymmdd}-notSynced`}
                          onClick={() => handleClickDailyQualityCell(yymmdd, "notSynced")}
                          aria-label={`${dailyDataQualityStatus.notSynced.length} participants not synced yet for this day`}
                          css={{
                            height: 24,
                            border:
                              selectedCell === `${type}-${yymmdd}-notSynced`
                                ? `2px solid ${color.border.black}`
                                : "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: fontSize.h7,
                            fontWeight: fontWeight.bold,
                            backgroundColor:
                              dailyDataQualityStatus.notSynced.length > 0
                                ? color.tag.grey.light
                                : "transparent",
                            cursor: "pointer",
                            ":hover": {
                              border: `2px solid ${color.border.black}`,
                            },
                          }}
                        >
                          {dailyDataQualityStatus.notSynced.length}
                        </button>
                      </div>
                    );
                  })}
                </div>
                {/* heat map */}
                <div
                  css={{
                    width: "100%",
                    paddingBottom: 16,
                    borderBottom: `1px solid ${color.border.light}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {participantActivated.map((p, i) => {
                    return (
                      <div
                        key={p.id}
                        css={{
                          width: "100%",
                          height: 16,
                          minHeight: 16,
                          display: "grid",
                          gridTemplateColumns: "17fr 56fr",
                        }}
                      >
                        <span
                          css={{
                            color: color.textIcon.secondary,
                            fontSize: fontSize.h7,
                            paddingLeft: 16,
                            width: "100%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {p.insignia ?? `P${(i + 1).toString().padStart(3, "0")}`}
                        </span>
                        <div
                          css={{
                            width: "100%",
                            display: "grid",
                            gridTemplateColumns: `repeat(7, 1fr)`,
                            gap: 2,
                          }}
                        >
                          {yymmddIndexList.map((yymmdd) => {
                            const activated = yymmdd >= p.activatedYymmdd;
                            return (
                              <HeatmapCell
                                key={`${p.id}-${yymmdd}`}
                                {...{
                                  id: `${type}-${p.id}-${yymmdd}`,
                                  type,
                                  wearTime: heatmapData[p.id]?.[yymmdd],
                                  activated,
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* legend */}
                <div
                  css={{
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 24,
                  }}
                >
                  {/* legend for different rage of wearTime */}
                  <div
                    css={{
                      width: 194,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <div
                      css={{
                        display: "grid",
                        gridTemplateColumns: "repeat(6, 1fr)",
                        width: "100%",
                        height: 8,
                        borderRadius: 2,
                        border: `1px solid ${color.border.dark}`,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        css={{
                          backgroundImage: `url(${HeatmapLowWearTimeBG})`,
                          backgroundSize: "cover",
                        }}
                      />
                      <div
                        css={{
                          backgroundColor:
                            type === "garminConnect" ? color.heatmap.gc._1 : color.heatmap.lf._1,
                        }}
                      />
                      <div
                        css={{
                          backgroundColor:
                            type === "garminConnect" ? color.heatmap.gc._2 : color.heatmap.lf._2,
                        }}
                      />
                      <div
                        css={{
                          backgroundColor:
                            type === "garminConnect" ? color.heatmap.gc._3 : color.heatmap.lf._3,
                        }}
                      />
                      <div
                        css={{
                          backgroundColor:
                            type === "garminConnect" ? color.heatmap.gc._4 : color.heatmap.lf._4,
                        }}
                      />
                      <div
                        css={{
                          backgroundColor:
                            type === "garminConnect" ? color.heatmap.gc._5 : color.heatmap.lf._5,
                        }}
                      />
                    </div>
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        css={{
                          color: color.textIcon.secondary,
                          fontSize: fontSize.h8,
                          width: 24,
                          textAlign: "center",
                        }}
                      >
                        4hr
                      </span>
                      <span
                        css={{
                          color: color.textIcon.secondary,
                          fontSize: fontSize.h8,
                          width: 24,
                          textAlign: "center",
                        }}
                      >
                        8hr
                      </span>
                      <span
                        css={{
                          color: color.textIcon.secondary,
                          fontSize: fontSize.h8,
                          width: 24,
                          textAlign: "center",
                        }}
                      >
                        12hr
                      </span>
                      <span
                        css={{
                          color: color.textIcon.secondary,
                          fontSize: fontSize.h8,
                          width: 24,
                          textAlign: "center",
                        }}
                      >
                        16hr
                      </span>
                      <span
                        css={{
                          color: color.textIcon.secondary,
                          fontSize: fontSize.h8,
                          width: 24,
                          textAlign: "center",
                        }}
                      >
                        20hr
                      </span>
                    </div>
                  </div>
                  {/* legend for not joined yet */}
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <div
                      css={{
                        width: 50,
                        height: 8,
                        border: `1px solid ${color.border.default}`,
                        borderRadius: 2,
                        backgroundImage: `url(${NotJoinedBG})`,
                        backgroundSize: "cover",
                      }}
                    />
                    <span css={{ fontSize: fontSize.h8, color: color.textIcon.secondary }}>
                      Not joined yet
                    </span>
                  </div>
                  {/* legend for not synced yet */}
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <img src={NotSyncedIcon} width={12} alt="Not synced yet icon" />
                    <span css={{ fontSize: fontSize.h8, color: color.textIcon.secondary }}>
                      Not synced yet
                    </span>
                  </div>
                </div>
              </>
            )}
          </>
        </OverviewBlockTemplate>
      </div>
    );
  };

  return (
    <div
      css={{
        width: "100vw",
        minHeight: "calc(100vh - 48px)",
        backgroundColor: color.background,
        position: "relative",
        "a:focus-visible, button:focus-visible, input:focus-visible": {
          outline: `2px solid ${color.primary}`,
          outlineOffset: "2px",
        },
      }}
    >
      {RIF(
        displaySummaryPage,
        <ProjectSummaryPage
          {...{
            setDisplaySummaryPage,
          }}
        />,
      )}
      <ProjectSwitchBar projectPanel="Overview" />
      {/* sidebar */}
      <OverviewSidebar
        {...{
          participantList,
          participantActivated,
          garminConnectEnabled,
          garminDeviceEnabled,
          expandSidebar,
          sidebarDate,
          sidebarDatatype,
          sidebarDefaultTab,
          sidebarInCellTabTitle,
          sidebarDeviceType,
          participantNotJoinedTheProject,
          participantNotSetupGarminConnect,
          participantNotSetupLabfrontDirect,
          participantInCell,
          setSendNotificationParticipantIdList,
          setDisplayPopupSendNotification,
          setExpandSidebar,
          setSelectedParticipantList,
        }}
      />
      {/* <SendNotificationParticipantSelector
        {...{
          participantList,
          unfold: true,
          setRenderParticipantSelector,
          setSendNotificationParticipantIdList,
          setDisplayPopupSendNotification,
          dataCollectedParticipantList: [],
          noDataYetParticipantList: [],
          participantSelectorHeader: 'test',
          participantSelectorDate: '250616',
          participantSelectorDatatype: AdherenceOverviewDataType.GarminDirect,
        }}
      /> */}
      {/* page content */}
      <div css={{ width: "100%", padding: 30 }}>
        {/* title: project & edit project detail button */}
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: 24,
            marginBottom: 27,
          }}
        >
          <p css={{ fontSize: fontSize.h3, fontWeight: fontWeight.thick }}>
            {project?.name ?? "Unknown Project"}
          </p>
          <ButtonReverse
            onClick={() => setDisplaySummaryPage(true)}
            {...{ btnPadding: "small", fontWeight: fontWeight.bold }}
          >
            View & Edit Project Details
          </ButtonReverse>
        </div>
        {/* content container */}
        <div
          css={{
            width: "100%",
            display: "inline-grid",
            gridTemplateColumns: "100fr 241fr",
            gap: 16,
          }}
        >
          {/* left side: task & participant overview */}
          <div
            css={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {RIF(participantList.length > 0, <ParticipantSetupStatus />)}
            {renderAttentionOverview && <AttentionOverview />}
            <TaskOverview />
          </div>
          {/* right side: garmin connect & garmin device overview */}
          {RIF(
            participantList.filter((p) => p.activated).length > 0,
            <div
              css={{
                display: "inline-grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
                width: "100%",
              }}
            >
              {/* garmin connect overview */}
              {RIF(
                garminConnectEnabled,
                <OverviewHeatmap
                  {...{
                    type: "garminConnect",
                    dailyStatus: garminConnectDailyStatus,
                    heatmapData: garminConnectHeatmapData,
                  }}
                />,
              )}
              {/* garmin device overview */}
              {RIF(
                garminDeviceBbiEnabled,
                <OverviewHeatmap
                  {...{
                    type: "garminDirect",
                    dailyStatus: garminDirectDailyStatus,
                    heatmapData: garminDirectHeatmapData,
                  }}
                />,
              )}
            </div>,
          )}
          {/* right side: empty state - no participant activated */}
          {RIF(
            participantList.length > 0 && participantList.filter((p) => p.activated).length === 0,
            <div
              css={{
                display: "inline-grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
                width: "100%",
              }}
            >
              {/* garmin connect overview */}
              {RIF(
                garminConnectEnabled,
                <OverviewHeatmap
                  {...{
                    type: "garminConnect",
                    dailyStatus: garminConnectDailyStatus,
                    heatmapData: garminConnectHeatmapData,
                    empty: true,
                  }}
                />,
              )}
              {/* garmin device overview */}
              {RIF(
                garminDeviceBbiEnabled,
                <OverviewHeatmap
                  {...{
                    type: "garminDirect",
                    dailyStatus: garminDirectDailyStatus,
                    heatmapData: garminDirectHeatmapData,
                    empty: true,
                  }}
                />,
              )}
            </div>,
          )}
          {/* right side: empty state - no participant */}
          {RIF(
            participantList.length === 0,
            <div
              css={{
                width: "100%",
                height: "72.8vh",
                minHeight: 573,
                borderRadius: 4,
                border: `1px dashed ${color.border.default}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <img
                src={InviteParticipantBackground}
                width="290"
                alt="Invite participants background illustration"
                aria-hidden="true"
              />
              <p
                css={{
                  fontSize: fontSize.h3,
                  marginTop: "40px",
                  marginBottom: "8px",
                }}
              >
                Invite participants to start collecting data.
              </p>
              <p
                css={{
                  color: color.textIcon.secondary,
                  marginBottom: 24,
                  maxWidth: 576,
                  textAlign: "center",
                }}
              >
                Add participants to your project by distributing a unique invite code generated in
                Labfront to each one.
              </p>
              <Button type="button" onClick={() => navigate(`/participants/${projectId}`)}>
                Invite Participant
              </Button>
            </div>,
          )}
        </div>
      </div>
    </div>
  );
};
